import axios from 'axios';

/**
 * RSS Feed Parser Utility
 * Parses RSS/Atom feeds and extracts article data
 */

// RSSApp widget ID
const RSSAPP_WIDGET_ID = '_pR8Eo8G0EoH0dxAJ';

// RSSApp API base URL
const RSSAPP_API_BASE = 'https://api.rss.app';

/**
 * Fetches RSS feed from RSSApp API
 * @param {string} widgetId - The RSSApp widget ID
 * @returns {Promise<Array>} - Parsed articles
 */
export const fetchRSSAppFeed = async (widgetId = RSSAPP_WIDGET_ID) => {
  try {
    // RSSApp provides a JSON feed API
    const response = await axios.get(`${RSSAPP_API_BASE}/v1/wall/${widgetId}/feed`, {
      params: {
        count: 20,
        include_content: true,
        include_media: true
      }
    });
    
    return parseRSSAppResponse(response.data);
  } catch (error) {
    console.error('Error fetching RSSApp feed:', error);
    // Try alternative RSSApp endpoint
    return tryAlternativeEndpoints(widgetId);
  }
};

/**
 * Try alternative RSSApp endpoints
 */
const tryAlternativeEndpoints = async (widgetId) => {
  const endpoints = [
    `${RSSAPP_API_BASE}/v1/groups/${widgetId}/feed`,
    `${RSSAPP_API_BASE}/v1/feeds/${widgetId}`,
  ];
  
  for (const endpoint of endpoints) {
    try {
      const response = await axios.get(endpoint, {
        params: {
          count: 20,
          include_content: true
        }
      });
      return parseRSSAppResponse(response.data);
    } catch (e) {
      console.warn(`Failed endpoint ${endpoint}:`, e);
      continue;
    }
  }
  
  throw new Error('Unable to fetch RSS feed from any endpoint');
};

/**
 * Parse RSSApp API response
 */
const parseRSSAppResponse = (data) => {
  if (!data || !data.items) {
    return [];
  }
  
  return data.items.map(item => ({
    id: item.id || item.guid || generateId(),
    title: item.title || 'Untitled',
    link: item.url || item.link || item.permalink,
    description: item.content_snippet || item.description || '',
    content: item.content || item.body || item.description || '',
    pubDate: item.published_at || item.pubDate || item.date || new Date().toISOString(),
    author: item.author || 'Unknown',
    image: extractImage(item),
    categories: item.categories || item.tags || [],
    source: 'RSSApp'
  }));
};

/**
 * Extract image from RSS item
 */
const extractImage = (item) => {
  if (item.image || item.media?.image) {
    return item.image || item.media.image;
  }
  
  // Try to extract from content
  if (item.content) {
    const imgMatch = item.content.match(/<img[^>]+src="([^">]+)"/);
    if (imgMatch) {
      return imgMatch[1];
    }
  }
  
  return null;
};

/**
 * Generate unique ID
 */
const generateId = () => {
  return Math.random().toString(36).substring(2, 15);
};

/**
 * Parse standard RSS/XML feed
 * @param {string} rssUrl - URL of RSS feed
 * @returns {Promise<Array>} - Parsed articles
 */
export const parseRSSFeed = async (rssUrl) => {
  try {
    // Fetch through backend to avoid CORS
    const response = await axios.get(`/api/rss-proxy?url=${encodeURIComponent(rssUrl)}`);
    const xmlText = response.data;
    
    return parseXMLFeed(xmlText);
  } catch (error) {
    console.error('Error parsing RSS feed:', error);
    throw error;
  }
};

/**
 * Parse XML/RSS feed string
 */
export const parseXMLFeed = (xmlString) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
  
  const items = xmlDoc.querySelectorAll('item');
  const articles = [];
  
  items.forEach(item => {
    const article = {
      id: item.querySelector('guid')?.textContent || 
           item.querySelector('link')?.textContent || 
           generateId(),
      title: item.querySelector('title')?.textContent || 'Untitled',
      link: item.querySelector('link')?.textContent || '',
      description: item.querySelector('description')?.textContent || 
                   item.querySelector('content\\:encoded')?.textContent || '',
      content: item.querySelector('content\\:encoded')?.textContent || 
               item.querySelector('description')?.textContent || '',
      pubDate: item.querySelector('pubDate')?.textContent || 
               item.querySelector('date')?.textContent || 
               new Date().toISOString(),
      author: item.querySelector('author')?.textContent || 
              item.querySelector('dc\\:creator')?.textContent || 
              'Unknown',
      image: extractImageFromXML(item),
      categories: Array.from(item.querySelectorAll('category')).map(cat => cat.textContent)
    };
    
    articles.push(article);
  });
  
  return articles;
};

/**
 * Extract image from XML item
 */
const extractImageFromXML = (item) => {
  // Try media:content
  const mediaContent = item.querySelector('media\\:content, media\\:thumbnail');
  if (mediaContent) {
    return mediaContent.getAttribute('url');
  }
  
  // Try enclosure
  const enclosure = item.querySelector('enclosure[type^="image"]');
  if (enclosure) {
    return enclosure.getAttribute('url');
  }
  
  // Try to extract from content
  const content = item.querySelector('content\\:encoded')?.textContent || 
                  item.querySelector('description')?.textContent || '';
  const imgMatch = content.match(/<img[^>]+src="([^">]+)"/);
  if (imgMatch) {
    return imgMatch[1];
  }
  
  return null;
};

/**
 * Format date for display
 */
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

/**
 * Truncate text
 */
export const truncateText = (text, maxLength = 150) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Strip HTML tags from text
 */
export const stripHTML = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Named exports
const rssParser = {
  fetchRSSAppFeed,
  parseRSSFeed,
  parseXMLFeed,
  formatDate,
  truncateText,
  stripHTML
};

export default rssParser;
